import { useState, useEffect } from "react"
import type { ReactNode } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { ArrowLeft, Car, Edit, Plus, Trash2, FileText, Download, Eye, DollarSign, MoreHorizontal, Stethoscope } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { buscarHistoricoFIPE, HistoricoFIPEBanco } from "@/lib/fipeService"
import { DOCUMENT_BUCKET, PHOTO_BUCKET, createSignedFileUrl } from "@/lib/vehicleStorage"
import { criarAvaliacaoParaVeiculo } from "@/lib/avaliacoes"
import { getPublicErrorMessage } from "@/lib/errors"
import { useAuth } from "@/contexts/AuthContext"
import { getLimite, CONTACT_EMAIL_URL, TRIAL_LIMIT } from "@/lib/planLimits"
import { useSubscription } from "@/hooks/useSubscription"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { getOficinaAtendimentoStatus, type OficinaAtendimentoStatus } from "@/lib/oficinaAtendimento"
import { ESTOQUE_STATUS_LABELS, getLojaComercialStatus, getRiscoCompra, type EstoqueStatus } from "@/lib/lojaComercial"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Veiculo {
  id: string
  placa: string
  marca: string
  modelo: string
  versao?: string | null
  ano: number
  anomodelo?: string | null
  cor?: string | null
  combustivel?: string | null
  cambio?: string | null
  quilometragem?: number | null
  motor?: string | null
  potencia?: string | null
  capacidadecarga?: number | null
  passageiros?: number | null
  tipoveiculo?: string | null
  cidade?: string | null
  estado?: string | null
  situacaoveiculo?: string | null
  portas?: number | null
  observacoes?: string | null
  chassi?: string | null
  renavam?: string | null
  documentacao_regularizada?: boolean | null
  foto_principal?: string | null
  estoque_status?: string | null
  created_at: string
  updated_at: string
}

interface Avaliacao {
  id: string
  status: string
  nota_final?: number | null
  objetivo_comercial?: string | null
  share_token?: string | null
  cliente_nome?: string | null
  cliente_telefone?: string | null
  cliente_assinado_em?: string | null
  atendimento_finalizado_em?: string | null
  created_at: string
}

interface Proposta {
  id: string
  avaliacao_id: string
  modalidade?: string | null
  status?: string | null
  share_token?: string | null
  valor_fipe?: number | null
  custo_reparos?: number | null
  valor_ofertado?: number | null
  margem_lucro?: number | null
  cliente_assinado_em?: string | null
  created_at: string
}

const isAvaliacaoAberta = (status: string) =>
  ["pendente", "ativa", "em_andamento"].includes(status)

interface Documento {
  id: string
  tipo_documento: string
  url: string
  nome_arquivo: string
  created_at: string
}

const humanizarValor = (valor: string | number | null | undefined, options?: { preservarCaixaAlta?: boolean }) => {
  if (valor === null || valor === undefined || valor === '') {
    return 'Não informado'
  }

  const texto = String(valor).trim()
  if (!texto) return 'Não informado'
  if (options?.preservarCaixaAlta) return texto.toUpperCase()

  const partes = texto
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\bgoias\b/g, 'goiás')
    .split('/')

  return partes
    .map((parte, index) => {
      if (index > 0 && parte.length <= 2) return parte.toUpperCase()
      return parte.replace(/(^|\s|-)\S/g, (letra) => letra.toUpperCase())
    })
    .join('/')
}

const valorAusente = (valor: string) => valor === 'Não informado'

const VeiculoDetalhes = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const mostrarFinanceiro = vertical.documentos.mostrarValoresFinanceiros
  const [veiculo, setVeiculo] = useState<Veiculo | null>(null)
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([])
  const [propostas, setPropostas] = useState<Proposta[]>([])
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [diagnosticos, setDiagnosticos] = useState<Array<{
    id: string
    avaliacao_id: string | null
    titulo: string | null
    status: string
    share_token: string | null
    cliente_ciente_em: string | null
    cliente_autorizou_em: string | null
    created_at: string
    diagnosticos_itens: Array<{ urgencia: string }>
  }>>([])
  const [historicoFIPE, setHistoricoFIPE] = useState<HistoricoFIPEBanco[]>([])
  const [documentPreview, setDocumentPreview] = useState<Documento | null>(null)
  const [loading, setLoading] = useState(true)
  const [startingEvaluation, setStartingEvaluation] = useState(false)
  const [limiteDialogOpen, setLimiteDialogOpen] = useState(false)
  const [limiteDialogTipo, setLimiteDialogTipo] = useState<'upgrade' | 'contato'>('upgrade')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const { isTrial, isCortesia, trialAvaliacoesRestantes } = useSubscription()

  useEffect(() => {
    if (id) {
      fetchVeiculoDetalhes()
    }
  }, [id])

  const fetchVeiculoDetalhes = async () => {
    if (!id) return

    try {
      // Fetch vehicle details
      const { data: veiculoData, error: veiculoError } = await supabase
        .from('veiculos')
        .select('*')
        .eq('id', id)
        .single()

      if (veiculoError) {
        toast.error("Erro ao carregar detalhes do veículo")
        navigate('/veiculos')
        return
      }

      const fotoPrincipalUrl = await createSignedFileUrl(PHOTO_BUCKET, veiculoData.foto_principal)
      setVeiculo({
        ...veiculoData,
        foto_principal: fotoPrincipalUrl ?? veiculoData.foto_principal,
      })

      // Fetch vehicle evaluations
      const { data: avaliacoesData, error: avaliacoesError } = await supabase
        .from('avaliacoes')
        .select('id, status, nota_final, objetivo_comercial, share_token, cliente_nome, cliente_telefone, cliente_assinado_em, atendimento_finalizado_em, created_at')
        .eq('veiculo_id', id)
        .order('created_at', { ascending: false })

      if (avaliacoesError) {
        console.error("Erro ao carregar avaliações:", avaliacoesError)
      } else {
        setAvaliacoes(avaliacoesData || [])

        if ((avaliacoesData || []).length > 0) {
          const avaliacaoIds = (avaliacoesData || []).map((avaliacao) => avaliacao.id)
          const { data: propostasData, error: propostasError } = await supabase
            .from('propostas')
            .select('id, avaliacao_id, modalidade, status, share_token, valor_fipe, custo_reparos, valor_ofertado, margem_lucro, cliente_assinado_em, created_at')
            .in('avaliacao_id', avaliacaoIds)
            .order('created_at', { ascending: false })

          if (propostasError) {
            console.error("Erro ao carregar fechamentos do veículo:", propostasError)
          } else {
            setPropostas(propostasData || [])
          }
        } else {
          setPropostas([])
        }
      }

      // Fetch vehicle documents
      const { data: documentosData, error: documentosError } = await supabase
        .from('documentos_veiculo')
        .select('*')
        .eq('veiculo_id', id)
        .order('created_at', { ascending: false })

      if (documentosError) {
        console.error("Erro ao carregar documentos:", documentosError)
      } else {
        const documentosResolvidos = await Promise.all((documentosData || []).map(async (documento) => ({
          ...documento,
          url: (await createSignedFileUrl(DOCUMENT_BUCKET, documento.url)) ?? documento.url,
        })))
        setDocumentos(documentosResolvidos)
      }

      if (mostrarFinanceiro) {
        await fetchHistoricoFIPE()
      } else {
        setHistoricoFIPE([])
      }

      if (!vertical.documentos.mostrarValoresFinanceiros) {
        const { data: diagData } = await supabase
          .from("diagnosticos")
          .select("id, avaliacao_id, titulo, status, share_token, cliente_ciente_em, cliente_autorizou_em, created_at, diagnosticos_itens(urgencia)")
          .eq("veiculo_id", id)
          .order("created_at", { ascending: false })
        setDiagnosticos((diagData ?? []) as Array<{
          id: string; avaliacao_id: string | null; titulo: string | null; status: string;
          share_token: string | null; cliente_ciente_em: string | null; cliente_autorizou_em: string | null; created_at: string;
          diagnosticos_itens: Array<{ urgencia: string }>
        }>)
      }
    } catch (error) {
      toast.error("Erro inesperado ao carregar dados")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  // Função para buscar histórico FIPE
  const fetchHistoricoFIPE = async () => {
    if (!id) return
    try {
      const historico = await buscarHistoricoFIPE(id)
      setHistoricoFIPE(historico)
      console.log('Histórico FIPE carregado:', historico)
    } catch (error) {
      console.error('Erro ao buscar histórico FIPE:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'concluida':
        return 'bg-green-100 text-green-800'
      case 'em_andamento':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'concluida':
        return 'Concluída'
      case 'em_andamento':
        return 'Em Andamento'
      default:
        return 'Pendente'
    }
  }

  const getTipoDocumentoText = (tipo: string) => {
    switch (tipo) {
      case 'crlv':
        return 'CRLV (Documento do Veículo)'
      case 'laudo':
        return 'Laudo de Vistoria'
      case 'ipva':
        return 'Comprovante IPVA'
      case 'seguro':
        return 'Apólice de Seguro'
      case 'outros':
        return 'Outros Documentos'
      default:
        return tipo
    }
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <FileText className="h-6 w-6 text-red-500" />;
    } else if (['jpg', 'jpeg', 'png'].includes(extension || '')) {
      return <FileText className="h-6 w-6 text-blue-500" />;
    }
    return <FileText className="h-6 w-6 text-gray-500" />;
  }

  const handleViewDocument = (documento: Documento) => {
    setDocumentPreview(documento)
  }

  const handleDownloadDocument = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error("Falha ao baixar arquivo")
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error("Erro ao baixar documento:", error)
      toast.error("Não foi possível baixar o documento")
    }
  }

  const isPreviewImage = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")
  }

  const isPreviewPdf = (fileName: string) => {
    return fileName.split('.').pop()?.toLowerCase() === "pdf"
  }

  const handleDeleteVeiculo = async () => {
    if (!id) return

    try {
      const avaliacaoIds = avaliacoes.map((avaliacao) => avaliacao.id)

      if (avaliacaoIds.length > 0) {
        const { error: propostasError } = await supabase
          .from('propostas')
          .delete()
          .in('avaliacao_id', avaliacaoIds)

        if (propostasError) {
          console.error("Erro ao deletar fechamentos relacionados:", propostasError)
          toast.error("Erro ao deletar fechamentos relacionados")
          return
        }

        const { error: fotosError } = await supabase
          .from('fotos_veiculo')
          .delete()
          .in('avaliacao_id', avaliacaoIds)

        if (fotosError) {
          console.error("Erro ao deletar fotos relacionadas:", fotosError)
          toast.error("Erro ao deletar fotos relacionadas")
          return
        }

        const { error: criteriosError } = await supabase
          .from('avaliacoes_criterios')
          .delete()
          .in('avaliacao_id', avaliacaoIds)

        if (criteriosError) {
          console.error("Erro ao deletar critérios relacionados:", criteriosError)
          toast.error("Erro ao deletar critérios relacionados")
          return
        }

        const { error: avaliacoesError } = await supabase
          .from('avaliacoes')
          .delete()
          .in('id', avaliacaoIds)

        if (avaliacoesError) {
          console.error("Erro ao deletar avaliações relacionadas:", avaliacoesError)
          toast.error("Erro ao deletar avaliações relacionadas")
          return
        }
      }

      const { error: documentosError } = await supabase
        .from('documentos_veiculo')
        .delete()
        .eq('veiculo_id', id)

      if (documentosError) {
        console.error("Erro ao deletar documentos relacionados:", documentosError)
        toast.error("Erro ao deletar documentos relacionados")
        return
      }

      const { error: historicoError } = await supabase
        .from('historico_fipe')
        .delete()
        .eq('veiculo_id', id)

      if (historicoError) {
        console.error("Erro ao deletar histórico FIPE relacionado:", historicoError)
        toast.error("Erro ao deletar histórico FIPE relacionado")
        return
      }

      const { error } = await supabase
        .from('veiculos')
        .delete()
        .eq('id', id)

      if (error) {
        toast.error("Erro ao deletar veículo")
        return
      }

      toast.success("Veículo deletado com sucesso")
      navigate('/veiculos')
    } catch (error) {
      toast.error("Erro inesperado ao deletar veículo")
      console.error("Erro:", error)
    }
  }

  const handleStartEvaluation = async (objetivoComercial?: "compra" | "consignacao") => {
    if (!veiculo?.id) {
      toast.error("Veículo não encontrado")
      return
    }

    if (!loja?.id) {
      toast.error("Loja não encontrada")
      return
    }

    setStartingEvaluation(true)

    try {
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      const avaliacao = await criarAvaliacaoParaVeiculo({
        lojaId: loja.id,
        veiculoId: veiculo.id,
        objetivoComercial,
        limiteAvaliacao: isCortesia ? undefined : isTrial ? TRIAL_LIMIT : getLimite(loja.plano),
        inicioPeriodo: isCortesia || isTrial ? undefined : startOfMonth,
      })

      toast.success(
        avaliacao.reusedExisting
          ? "Abrindo avaliação em andamento"
          : "Avaliação iniciada com sucesso!"
      )
      navigate(`/avaliacao/${avaliacao.id}`)
    } catch (error) {
      const code = (error as any)?.code
      if (code === "LIMITE_TRIAL" || code === "LIMITE_ATINGIDO") {
        const isLojaProPlan = loja?.plano === 'pro' && code === "LIMITE_ATINGIDO"
        setLimiteDialogTipo(isLojaProPlan ? 'contato' : 'upgrade')
        setLimiteDialogOpen(true)
        return
      }
      console.error("Erro ao iniciar avaliação:", error)
      toast.error(getPublicErrorMessage(error instanceof Error ? error.message : null, "Não foi possível iniciar a avaliação agora."))
    } finally {
      setStartingEvaluation(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!veiculo) {
    return (
      <div className="text-center py-16">
        <h3 className="text-lg font-semibold mb-2">Veículo não encontrado</h3>
        <Button asChild>
          <Link to="/veiculos">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para Veículos
          </Link>
        </Button>
      </div>
    )
  }

  const avaliacaoAberta = avaliacoes.find((avaliacao) => isAvaliacaoAberta(avaliacao.status))
  const ultimaAvaliacaoConcluida = avaliacoes.find((avaliacao) => avaliacao.status === "concluida")
  const atendimentoOficinaAvaliacao = !mostrarFinanceiro
    ? avaliacaoAberta ?? ultimaAvaliacaoConcluida ?? avaliacoes[0] ?? null
    : null
  const diagnosticosDoAtendimento = atendimentoOficinaAvaliacao
    ? diagnosticos.filter((diagnostico) => diagnostico.avaliacao_id === atendimentoOficinaAvaliacao.id || !diagnostico.avaliacao_id)
    : diagnosticos
  const diagnosticoPrincipal = diagnosticosDoAtendimento[0] ?? diagnosticos[0] ?? null
  const atendimentoOficina = !mostrarFinanceiro
    ? getOficinaAtendimentoStatus(atendimentoOficinaAvaliacao, diagnosticos)
    : null
  const propostaDaUltimaAvaliacaoConcluida = ultimaAvaliacaoConcluida
    ? propostas.find((proposta) => proposta.avaliacao_id === ultimaAvaliacaoConcluida.id)
    : undefined
  const avaliacaoComercialAtual = mostrarFinanceiro
    ? avaliacaoAberta ?? ultimaAvaliacaoConcluida ?? avaliacoes[0] ?? null
    : null
  const propostaComercialAtual = mostrarFinanceiro && avaliacaoComercialAtual
    ? propostas.find((proposta) => proposta.avaliacao_id === avaliacaoComercialAtual.id) ?? propostaDaUltimaAvaliacaoConcluida ?? null
    : null
  const statusComercial = mostrarFinanceiro
    ? getLojaComercialStatus(avaliacaoComercialAtual, propostaComercialAtual, veiculo.estoque_status)
    : null
  const riscoCompra = propostaComercialAtual
    ? getRiscoCompra(propostaComercialAtual.valor_fipe, propostaComercialAtual.custo_reparos)
    : null
  const registroLabel = vertical.labels.labelPrincipal.toLowerCase()
  const documentoLabel = vertical.labels.documentoFinal.toLowerCase()
  const resumoExclusao = [
    `${avaliacoes.length} avaliação${avaliacoes.length !== 1 ? "ões" : ""}`,
    `${propostas.length} fechamento${propostas.length !== 1 ? "s" : ""}`,
    `${documentos.length} documento${documentos.length !== 1 ? "s" : ""}`,
    mostrarFinanceiro ? `${historicoFIPE.length} consulta${historicoFIPE.length !== 1 ? "s" : ""} FIPE` : null,
  ].filter(Boolean).join(" • ")
  const ctaAvaliacaoLabel = startingEvaluation
    ? avaliacaoAberta
      ? "Abrindo..."
      : "Criando..."
    : avaliacaoAberta
      ? vertical.labels.continuarAcao
      : vertical.labels.iniciarAcao
  const descricaoAvaliacaoAberta = avaliacaoAberta
    ? `Já existe uma ${registroLabel} em andamento para este veículo.`
    : null
  const etapaFluxoAtual = avaliacaoAberta
    ? `${vertical.labels.labelPrincipal} em andamento`
    : propostaDaUltimaAvaliacaoConcluida
      ? `${vertical.labels.documentoFinal} gerado`
      : ultimaAvaliacaoConcluida
        ? `${vertical.labels.labelPrincipal} concluída`
        : "Veículo cadastrado"
  const proximoPassoLabel = startingEvaluation
    ? ctaAvaliacaoLabel
    : avaliacaoAberta
      ? vertical.labels.continuarAcao
      : ultimaAvaliacaoConcluida
        ? propostaDaUltimaAvaliacaoConcluida
          ? vertical.labels.verDocumento
          : vertical.labels.criarDocumento
        : vertical.labels.iniciarAcao
  const etapaOperacionalLabel = atendimentoOficina?.label ?? etapaFluxoAtual
  const proximoPassoOperacionalLabel = startingEvaluation
    ? ctaAvaliacaoLabel
    : atendimentoOficina?.proximaAcao ?? statusComercial?.proximaAcao ?? proximoPassoLabel
  const avaliacoesLabel = `${avaliacoes.length} ${avaliacoes.length === 1 ? registroLabel : vertical.labels.unidadeMensal}`
  const dataCadastroLabel = `Cadastrado em ${new Date(veiculo.created_at).toLocaleDateString('pt-BR')}`
  const planoResumo = isTrial
    ? `${trialAvaliacoesRestantes} ${trialAvaliacoesRestantes === 1 ? registroLabel : vertical.labels.unidadeMensal} restantes`
    : isCortesia
      ? "Plano cortesia"
      : `Plano ${loja?.plano || 'ativo'}`
  const quilometragemLabel = veiculo.quilometragem !== null && veiculo.quilometragem !== undefined
    ? `${veiculo.quilometragem.toLocaleString('pt-BR')} km`
    : 'Não informado'
  const localizacaoLabel = veiculo.cidade || veiculo.estado
    ? `${humanizarValor(veiculo.cidade)}${veiculo.cidade && veiculo.estado ? '/' : ''}${veiculo.estado ? humanizarValor(veiculo.estado, { preservarCaixaAlta: true }) : ''}`
    : 'Não informado'

  const renderInfoField = (label: string, value: string, options?: { mono?: boolean }) => (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={valorAusente(value)
        ? "text-sm text-muted-foreground/70"
        : options?.mono
          ? "font-mono text-xs text-foreground"
          : "text-sm text-foreground"
      }>
        {value}
      </p>
    </div>
  )

  const renderInfoGroup = (title: string, children: ReactNode) => (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <Separator className="flex-1" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {children}
      </div>
    </section>
  )

  const handlePrimaryFlowAction = () => {
    if (!mostrarFinanceiro && atendimentoOficina) {
      void handleOficinaPrimaryAction(atendimentoOficina.status)
      return
    }

    if (mostrarFinanceiro && statusComercial) {
      void handleLojaPrimaryAction()
      return
    }

    if (avaliacaoAberta) {
      navigate(`/avaliacao/${avaliacaoAberta.id}`)
      return
    }

    if (ultimaAvaliacaoConcluida) {
      navigate(`/proposta/${ultimaAvaliacaoConcluida.id}`)
      return
    }

    void handleStartEvaluation()
  }

  const handleLojaPrimaryAction = async () => {
    if (!veiculo?.id || !statusComercial) return

    if (statusComercial.status === "veiculo_cadastrado") {
      await handleStartEvaluation("compra")
      return
    }

    if (statusComercial.status === "avaliacao_iniciada" && avaliacaoComercialAtual) {
      navigate(`/avaliacao/${avaliacaoComercialAtual.id}`)
      return
    }

    if ((statusComercial.status === "avaliacao_concluida" || statusComercial.status === "proposta_preparada" || statusComercial.status === "proposta_enviada") && avaliacaoComercialAtual) {
      navigate(`/proposta/${avaliacaoComercialAtual.id}`)
      return
    }

    if (statusComercial.status === "proposta_aceita") {
      const novoStatus = statusComercial.tipo === "consignacao" ? "consignado_ativo" : "comprado"
      const { error } = await supabase
        .from("veiculos")
        .update({ estoque_status: novoStatus, updated_at: new Date().toISOString() })
        .eq("id", veiculo.id)
      if (error) {
        toast.error("Não foi possível atualizar o status do estoque")
        return
      }
      toast.success(statusComercial.tipo === "consignacao" ? "Consignado ativado" : "Veículo marcado como comprado")
      await fetchVeiculoDetalhes()
    }
  }

  const handleAtualizarEstoque = async (estoqueStatus: EstoqueStatus) => {
    if (!veiculo?.id) return
    const { error } = await supabase
      .from("veiculos")
      .update({ estoque_status: estoqueStatus, updated_at: new Date().toISOString() })
      .eq("id", veiculo.id)
    if (error) {
      toast.error("Não foi possível atualizar o status do estoque")
      return
    }
    toast.success("Status do estoque atualizado")
    await fetchVeiculoDetalhes()
  }

  const handleOficinaPrimaryAction = async (status: OficinaAtendimentoStatus) => {
    if (!veiculo?.id) return

    if (status === "sem_entrada") {
      await handleStartEvaluation()
      return
    }

    if (status === "entrada_criada" && atendimentoOficinaAvaliacao) {
      navigate(`/avaliacao/${atendimentoOficinaAvaliacao.id}`)
      return
    }

    if (status === "cliente_ciente_termo" && atendimentoOficinaAvaliacao) {
      const { error } = await supabase
        .from("avaliacoes")
        .update({
          atendimento_finalizado_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", atendimentoOficinaAvaliacao.id)
      if (error) {
        toast.error("Não foi possível finalizar o atendimento")
        return
      }
      toast.success("Atendimento finalizado")
      await fetchVeiculoDetalhes()
      return
    }

    if ((status === "entrada_finalizada" || status === "termo_emitido") && atendimentoOficinaAvaliacao) {
      if (status === "entrada_finalizada") {
        navigate(`/proposta/${atendimentoOficinaAvaliacao.id}`)
        return
      }
      if (!diagnosticoPrincipal) {
        navigate(`/diagnostico/novo?veiculo=${veiculo.id}&avaliacao=${atendimentoOficinaAvaliacao.id}`)
        return
      }
    }

    if (status === "diagnostico_criado" && diagnosticoPrincipal) {
      navigate(`/diagnostico/${diagnosticoPrincipal.id}`)
      return
    }

    if ((status === "diagnostico_enviado" || status === "cliente_ciente_diagnostico" || status === "cliente_autorizou") && diagnosticoPrincipal) {
      if (status === "diagnostico_enviado") {
        navigate(`/diagnostico/${diagnosticoPrincipal.id}`)
        return
      }

      if (atendimentoOficinaAvaliacao && atendimentoOficinaAvaliacao.status !== "concluida") {
        const { error } = await supabase
          .from("avaliacoes")
          .update({ status: "concluida", updated_at: new Date().toISOString() })
          .eq("id", atendimentoOficinaAvaliacao.id)
        if (error) {
          toast.error("Não foi possível finalizar o atendimento")
          return
        }
        toast.success("Atendimento finalizado")
        await fetchVeiculoDetalhes()
      } else {
        navigate(`/diagnostico/${diagnosticoPrincipal.id}`)
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/veiculos">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">{veiculo.placa}</h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {humanizarValor(veiculo.marca)} {humanizarValor(veiculo.modelo)} - {veiculo.ano}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="font-normal">Veículo cadastrado</Badge>
              <Badge variant="outline" className="font-normal">{avaliacoesLabel}</Badge>
              <Badge variant="outline" className="font-normal">{dataCadastroLabel}</Badge>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
          <Button size="sm" className="flex-1 sm:flex-initial" onClick={handlePrimaryFlowAction} disabled={startingEvaluation}>
            <>
              {avaliacaoAberta || ultimaAvaliacaoConcluida ? (
                <FileText className="h-4 w-4 mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {proximoPassoOperacionalLabel}
            </>
          </Button>
          {!mostrarFinanceiro && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-initial"
              onClick={() => navigate(`/diagnostico/novo?veiculo=${veiculo.id}${atendimentoOficinaAvaliacao ? `&avaliacao=${atendimentoOficinaAvaliacao.id}` : ""}`)}
            >
              <Stethoscope className="h-4 w-4 mr-2" />
              Novo diagnóstico
            </Button>
          )}
          {mostrarFinanceiro && !avaliacaoComercialAtual && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial"
                onClick={() => void handleStartEvaluation("compra")}
                disabled={startingEvaluation}
              >
                Avaliar para compra
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 sm:flex-initial"
                onClick={() => void handleStartEvaluation("consignacao")}
                disabled={startingEvaluation}
              >
                Receber em consignação
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" asChild className="flex-1 sm:flex-initial">
            <Link to={`/editar-veiculo/${veiculo.id}`}>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Link>
          </Button>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Ao excluir o veículo{" "}
                  <span className="font-medium text-foreground">{veiculo.placa}</span>, todas as
                  avaliações, fechamentos, fotos e documentos relacionados também serão removidos.
                  Esta ação não pode ser desfeita.
                  <span className="mt-2 block text-xs text-muted-foreground">
                    Impacto atual: {resumoExclusao}
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteVeiculo}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Mais opções</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/editar-veiculo/${veiculo.id}#documentos-veiculo`}>
                  <FileText className="mr-2 h-4 w-4" />
                  Gerenciar documentos
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={(event) => {
                  event.preventDefault()
                  setDeleteDialogOpen(true)
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir veículo
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Foto Principal do Veículo */}
      {veiculo.foto_principal && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Foto Principal do Veículo
            </CardTitle>
            <CardDescription>
              Foto principal selecionada para identificação do veículo
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <div className="aspect-[4/3] w-full max-w-md rounded-md border overflow-hidden bg-black/5">
              <img
                src={veiculo.foto_principal}
                alt={`Foto principal do veículo ${veiculo.placa}`}
                className="w-full h-full object-cover"
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        {/* Vehicle Details and Documents */}
        <div className="xl:col-span-2 space-y-6">
          <Card className="border-primary/15 bg-primary/5">
            <CardContent className="grid gap-4 p-4 sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Status atual</p>
                <p className="text-sm font-semibold text-foreground">{statusComercial?.label ?? etapaOperacionalLabel}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Próxima ação</p>
                <p className="text-sm font-semibold text-foreground">{proximoPassoOperacionalLabel}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">{mostrarFinanceiro ? "Plano" : "Cliente"}</p>
                <p className="text-sm font-semibold text-foreground">
                  {mostrarFinanceiro
                    ? planoResumo
                    : atendimentoOficinaAvaliacao?.cliente_nome || atendimentoOficinaAvaliacao?.cliente_telefone || "Não informado"}
                </p>
              </div>
              {mostrarFinanceiro && (
                <>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Tipo comercial</p>
                    <p className="text-sm font-semibold text-foreground">
                      {statusComercial?.tipo === "consignacao" ? "Consignação" : "Compra para estoque"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Estoque</p>
                    <p className="text-sm font-semibold text-foreground">
                      {ESTOQUE_STATUS_LABELS[(veiculo.estoque_status as EstoqueStatus) || "avaliado"] ?? "Avaliado"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Documento</p>
                    <p className="text-sm font-semibold text-foreground">
                      {propostaComercialAtual?.cliente_assinado_em
                        ? "Assinado"
                        : propostaComercialAtual?.share_token
                          ? "Enviado"
                          : propostaComercialAtual
                            ? "Preparado"
                            : "Pendente"}
                    </p>
                  </div>
                  {statusComercial?.helper && (
                    <p className="text-xs text-muted-foreground sm:col-span-3">{statusComercial.helper}</p>
                  )}
                  {veiculo.estoque_status === "comprado" && (
                    <div className="flex flex-wrap gap-2 sm:col-span-3">
                      <Button size="sm" variant="outline" onClick={() => void handleAtualizarEstoque("em_preparacao")}>Em preparação</Button>
                      <Button size="sm" variant="outline" onClick={() => void handleAtualizarEstoque("anunciado")}>Anunciado</Button>
                      <Button size="sm" variant="outline" onClick={() => void handleAtualizarEstoque("vendido")}>Marcar vendido</Button>
                    </div>
                  )}
                  {veiculo.estoque_status === "consignado_ativo" && (
                    <div className="flex flex-wrap gap-2 sm:col-span-3">
                      <Button size="sm" variant="outline" onClick={() => void handleAtualizarEstoque("vendido")}>Marcar vendido</Button>
                      <Button size="sm" variant="outline" onClick={() => void handleAtualizarEstoque("devolvido")}>Encerrar/devolver</Button>
                    </div>
                  )}
                </>
              )}
              {!mostrarFinanceiro && (
                <>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Entrada vinculada</p>
                    <p className="text-sm font-semibold text-foreground">
                      {atendimentoOficinaAvaliacao ? new Date(atendimentoOficinaAvaliacao.created_at).toLocaleDateString("pt-BR") : "Não iniciada"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Termo</p>
                    <p className="text-sm font-semibold text-foreground">
                      {atendimentoOficinaAvaliacao?.cliente_assinado_em
                        ? "Cliente assinou"
                        : atendimentoOficinaAvaliacao?.share_token
                          ? "Enviado"
                          : "Pendente"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Diagnóstico</p>
                    <p className="text-sm font-semibold text-foreground">
                      {diagnosticoPrincipal?.cliente_autorizou_em || diagnosticoPrincipal?.status === "autorizado"
                        ? "Autorizado"
                        : diagnosticoPrincipal?.cliente_ciente_em || diagnosticoPrincipal?.status === "ciente"
                          ? "Cliente ciente"
                          : diagnosticoPrincipal?.share_token || diagnosticoPrincipal?.status === "compartilhado"
                            ? "Enviado"
                            : diagnosticoPrincipal
                              ? "Criado"
                              : "Pendente"}
                    </p>
                  </div>
                  {atendimentoOficina?.helper && (
                    <p className="text-xs text-muted-foreground sm:col-span-3">{atendimentoOficina.helper}</p>
                  )}
                  {atendimentoOficina?.status === "cliente_ciente_termo" && (
                    <button
                      type="button"
                      onClick={() => navigate(`/diagnostico/novo?veiculo=${veiculo.id}&avaliacao=${atendimentoOficinaAvaliacao?.id}`)}
                      className="text-left text-xs font-medium text-primary hover:underline sm:col-span-3"
                    >
                      Criar diagnóstico se necessário
                    </button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {mostrarFinanceiro && propostaComercialAtual && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {propostaComercialAtual.modalidade === "consignacao" ? "Controle da consignação" : "Decisão de compra"}
                </CardTitle>
                <CardDescription>
                  {propostaComercialAtual.modalidade === "consignacao"
                    ? "Resumo comercial do termo e próxima ação."
                    : "FIPE, reparos, oferta e risco para apoiar a negociação."}
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {propostaComercialAtual.modalidade === "consignacao" ? (
                  <>
                    {renderInfoField("Valor anunciado", propostaComercialAtual.valor_ofertado ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(propostaComercialAtual.valor_ofertado) : "Não informado")}
                    {renderInfoField("Termo", propostaComercialAtual.cliente_assinado_em ? "Assinado" : propostaComercialAtual.share_token ? "Enviado" : "Preparado")}
                    {renderInfoField("Status", statusComercial?.label ?? "Consignação")}
                    {renderInfoField("Estoque", ESTOQUE_STATUS_LABELS[(veiculo.estoque_status as EstoqueStatus) || "avaliado"] ?? "Avaliado")}
                  </>
                ) : (
                  <>
                    {renderInfoField("FIPE", propostaComercialAtual.valor_fipe ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(propostaComercialAtual.valor_fipe) : "Não informado")}
                    {renderInfoField("Reparos estimados", new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(propostaComercialAtual.custo_reparos ?? 0))}
                    {renderInfoField("Oferta", propostaComercialAtual.valor_ofertado ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(propostaComercialAtual.valor_ofertado) : "Não informado")}
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Risco</p>
                      <Badge variant="outline" className={riscoCompra?.className}>{riscoCompra?.label ?? "Não calculado"}</Badge>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                Informações do Veículo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {renderInfoGroup("Identificação", (
                <>
                  {renderInfoField("Placa", humanizarValor(veiculo.placa, { preservarCaixaAlta: true }), { mono: true })}
                  {renderInfoField("Chassi", humanizarValor(veiculo.chassi, { preservarCaixaAlta: true }), { mono: true })}
                  {renderInfoField("RENAVAM", humanizarValor(veiculo.renavam, { preservarCaixaAlta: true }), { mono: true })}
                </>
              ))}

              {renderInfoGroup("Especificações", (
                <>
                  {renderInfoField("Versão", humanizarValor(veiculo.versao))}
                  {renderInfoField("Cor", humanizarValor(veiculo.cor))}
                  {renderInfoField("Combustível", humanizarValor(veiculo.combustivel))}
                  {renderInfoField("Câmbio", humanizarValor(veiculo.cambio))}
                  {renderInfoField("Potência", humanizarValor(veiculo.potencia))}
                  {renderInfoField("Motor", humanizarValor(veiculo.motor))}
                  {renderInfoField("Portas", veiculo.portas ? `${veiculo.portas} portas` : 'Não informado')}
                  {renderInfoField("Passageiros", veiculo.passageiros ? `${veiculo.passageiros} pessoas` : 'Não informado')}
                  {renderInfoField("Ano modelo", humanizarValor(veiculo.anomodelo))}
                  {renderInfoField("Quilometragem", quilometragemLabel)}
                  {renderInfoField("Capacidade de carga", veiculo.capacidadecarga ? `${veiculo.capacidadecarga} kg` : 'Não informado')}
                </>
              ))}

              {renderInfoGroup("Localização e status", (
                <>
                  {renderInfoField("Cidade/Estado", localizacaoLabel)}
                  {renderInfoField("Tipo de veículo", humanizarValor(veiculo.tipoveiculo))}
                  {renderInfoField("Situação", humanizarValor(veiculo.situacaoveiculo))}
                </>
              ))}

              {renderInfoGroup("Observações", (
                <div className="sm:col-span-2 lg:col-span-3">
                  {renderInfoField("Observações", humanizarValor(veiculo.observacoes))}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Vehicle Documents */}
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documentos do Veículo
                </CardTitle>
                <CardDescription>
                  {documentos.length === 0
                    ? "Nenhum documento anexado ainda"
                    : `${documentos.length} documento${documentos.length > 1 ? 's' : ''} anexado${documentos.length > 1 ? 's' : ''}`
                  }
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/editar-veiculo/${veiculo.id}#documentos-veiculo`}>
                  <FileText className="mr-2 h-4 w-4" />
                  Gerenciar documentos
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {documentos.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  Este veículo ainda não possui documentos anexados.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {documentos.map((documento) => (
                    <Card key={documento.id} className="group hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3 mb-4">
                          {getFileIcon(documento.nome_arquivo)}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {getTipoDocumentoText(documento.tipo_documento)}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {documento.nome_arquivo}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(documento.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleViewDocument(documento)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Visualizar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => handleDownloadDocument(documento.url, documento.nome_arquivo)}
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Baixar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!documentPreview} onOpenChange={(open) => !open && setDocumentPreview(null)}>
            <DialogContent className="max-h-[90vh] max-w-5xl overflow-hidden p-0">
              {documentPreview && (
                <>
                  <DialogHeader className="border-b border-border/70 px-6 py-4">
                    <DialogTitle className="pr-8 text-base">
                      {getTipoDocumentoText(documentPreview.tipo_documento)}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">{documentPreview.nome_arquivo}</p>
                  </DialogHeader>
                  <div className="flex max-h-[calc(90vh-5rem)] items-center justify-center bg-muted/30 p-4">
                    {isPreviewImage(documentPreview.nome_arquivo) ? (
                      <img
                        src={documentPreview.url}
                        alt={documentPreview.nome_arquivo}
                        className="max-h-[calc(90vh-8rem)] max-w-full rounded-lg object-contain"
                      />
                    ) : isPreviewPdf(documentPreview.nome_arquivo) ? (
                      <iframe
                        src={documentPreview.url}
                        title={documentPreview.nome_arquivo}
                        className="h-[70vh] w-full rounded-lg border border-border/70 bg-white"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-4 py-12 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">Pré-visualização não disponível</p>
                          <p className="text-sm text-muted-foreground">
                            Esse tipo de arquivo não pode ser exibido no modal.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadDocument(documentPreview.url, documentPreview.nome_arquivo)}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Baixar arquivo
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

        </div>

        {/* Evaluations History */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{mostrarFinanceiro ? "Histórico de Avaliações" : "Histórico de entradas"}</CardTitle>
              <CardDescription>
                {avaliacoes.length === 0 
                  ? (mostrarFinanceiro ? "Nenhuma avaliação realizada ainda" : "Nenhuma entrada registrada ainda")
                  : `${avaliacoes.length} ${mostrarFinanceiro ? `avaliação${avaliacoes.length > 1 ? 'ões' : ''}` : `entrada${avaliacoes.length > 1 ? 's' : ''}`} registrada${avaliacoes.length > 1 ? 's' : ''}`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {avaliacoes.length === 0 ? (
                <div className="rounded-md border border-dashed p-4">
                  <p className="text-sm text-muted-foreground">{mostrarFinanceiro ? "Nenhuma avaliação realizada ainda" : "Nenhuma entrada registrada ainda"}</p>
                  <button
                    type="button"
                    onClick={handlePrimaryFlowAction}
                    disabled={startingEvaluation}
                    className="mt-2 text-sm font-medium text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
                  >
                    Iniciar primeira {registroLabel}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-lg border bg-muted/25 p-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">
                        {avaliacaoAberta
                          ? `Existe uma ${registroLabel} em andamento`
                          : ultimaAvaliacaoConcluida
                            ? propostaDaUltimaAvaliacaoConcluida
                              ? `O ${documentoLabel} deste veículo já foi criado`
                              : `A ${registroLabel} terminou e já pode virar ${documentoLabel}`
                            : `Pronto para iniciar a primeira ${registroLabel}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {avaliacaoAberta
                          ? descricaoAvaliacaoAberta
                          : ultimaAvaliacaoConcluida
                            ? propostaDaUltimaAvaliacaoConcluida
                              ? `Abra o ${documentoLabel} para revisar, ajustar ou gerar o PDF.`
                              : `O próximo passo natural é gerar o ${documentoLabel} desta ${registroLabel} concluída.`
                            : `Depois do cadastro, o fluxo principal segue direto para a ${registroLabel}.`}
                      </p>
                    </div>
                  </div>
                  {avaliacoes.map((avaliacao) => (
                    <div key={avaliacao.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={getStatusColor(avaliacao.status)}>
                          {getStatusText(avaliacao.status)}
                        </Badge>
                        {avaliacaoAberta?.id === avaliacao.id && (
                          <Badge variant="secondary" className="bg-primary/10 text-primary">
                            Em andamento
                          </Badge>
                        )}
                        {avaliacao.nota_final && (
                          <span className="text-sm font-medium">
                            Nota: {avaliacao.nota_final.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(avaliacao.created_at).toLocaleDateString('pt-BR')}
                      </p>
                      <Button variant="link" size="sm" className="p-0 h-auto mt-2" asChild>
                        <Link to={`/avaliacao/${avaliacao.id}`}>
                          Ver Detalhes
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {!mostrarFinanceiro && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Stethoscope className="h-4 w-4" />
                    Diagnósticos técnicos
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/diagnostico/novo?veiculo=${veiculo.id}${atendimentoOficinaAvaliacao ? `&avaliacao=${atendimentoOficinaAvaliacao.id}` : ""}`)}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Novo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {diagnosticos.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Stethoscope className="h-8 w-8 text-muted-foreground opacity-25" />
                    <p className="text-sm text-muted-foreground">Nenhum diagnóstico registrado.</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => navigate(`/diagnostico/novo?veiculo=${veiculo.id}${atendimentoOficinaAvaliacao ? `&avaliacao=${atendimentoOficinaAvaliacao.id}` : ""}`)}
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Registrar problema encontrado
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {diagnosticos.map((d) => {
                      const urgentes = d.diagnosticos_itens.filter(i => i.urgencia === "urgente").length
                      const total    = d.diagnosticos_itens.length
                      return (
                        <div
                          key={d.id}
                          className="flex items-center justify-between rounded-lg border p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => navigate(`/diagnostico/${d.id}`)}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {d.titulo ?? "Diagnóstico Técnico"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {total} {total === 1 ? "item" : "itens"}
                              {urgentes > 0 && (
                                <span className="ml-2 text-red-600 font-medium">
                                  · {urgentes} urgente{urgentes > 1 ? "s" : ""}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {d.status === "compartilhado" && (
                              <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                Compartilhado
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(d.created_at).toLocaleDateString("pt-BR")}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {mostrarFinanceiro && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Histórico FIPE
                </CardTitle>
                <CardDescription>
                  {historicoFIPE.length === 0
                    ? "Nenhuma consulta FIPE realizada ainda"
                    : `${historicoFIPE.length} consulta${historicoFIPE.length > 1 ? 's' : ''} registrada${historicoFIPE.length > 1 ? 's' : ''}`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historicoFIPE.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="rounded-full bg-muted p-4 mx-auto mb-4 w-fit">
                      <DollarSign className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Este veículo ainda não possui consultas FIPE
                    </p>
                    <p className="text-xs text-muted-foreground">
                      As consultas FIPE são salvas automaticamente durante o cadastro e quando fechamentos são criados
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historicoFIPE.map((consulta, index) => (
                      <div key={consulta.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-lg text-blue-600">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            }).format(consulta.valor_fipe)}
                          </span>
                          {index === 0 && (
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              Mais Recente
                            </Badge>
                          )}
                          <Badge variant="outline">
                            {consulta.origem === 'manual' ? 'Manual' : 'Automático'}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>Placa consultada: {consulta.placa_consultada}</p>
                          <p>Data: {new Date(consulta.data_consulta).toLocaleDateString('pt-BR')}</p>
                          <p>Hora: {new Date(consulta.data_consulta).toLocaleTimeString('pt-BR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dialog: limite de avaliações atingido */}
          <Dialog open={limiteDialogOpen} onOpenChange={setLimiteDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {limiteDialogTipo === 'contato' ? 'Limite do Plano Pro atingido' : 'Limite atingido'}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                {limiteDialogTipo === 'contato'
                  ? 'Você atingiu as 60 avaliações do mês. Precisa de mais volume? Entre em contato.'
                  : isTrial
                    ? 'Você usou suas 5 avaliações gratuitas. Assine um plano para continuar avaliando.'
                    : 'Você usou todas as avaliações do Plano Básico neste mês. Faça upgrade para o Plano Pro e tenha 60 avaliações.'}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                {limiteDialogTipo === 'contato' ? (
                  <a
                    href={CONTACT_EMAIL_URL}
                    className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    Enviar e-mail
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setLimiteDialogOpen(false); navigate('/planos') }}
                    className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    Ver planos
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setLimiteDialogOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Fechar
                </button>
              </div>
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </div>
  )
}

export default VeiculoDetalhes
